import React, { useState } from "react";
import brandLogo from "../../assets/logo.png";
import {
  LoginVariant,
  slashAccentClass,
  visualBgClass,
} from "./loginStyles";

interface LoginShellProps {
  title: string;
  subtitle?: string;
  variant?: LoginVariant;
  children: React.ReactNode;
}

const VARIANT_COPY: Record<LoginVariant, { label: string; line: string }> = {
  branch: {
    label: "Branch workspace",
    line: "Run sales, inventory, and floor operations from one place.",
  },
  admin: {
    label: "Admin console",
    line: "Configure staff, permissions, and system-wide settings.",
  },
  company: {
    label: "Company portal",
    line: "Oversee shops, subscriptions, and company accounts.",
  },
};

function LoginShell({
  title,
  subtitle,
  variant = "branch",
  children,
}: LoginShellProps) {
  const copy = VARIANT_COPY[variant];
  const slash = slashAccentClass[variant];
  const [logoFailed, setLogoFailed] = useState(false);

  return (
    <div className="min-h-screen min-h-dvh w-full bg-login-panel font-inter text-login-ink">
      <div className="grid min-h-screen min-h-dvh w-full grid-cols-1 min-[901px]:grid-cols-[1.05fr_0.95fr]">
        <aside
          className={[
            "relative flex items-end overflow-hidden px-6 py-8 text-[#f2f4f3] motion-safe:animate-login-visual-in max-[900px]:min-h-[220px] max-[900px]:px-6 max-[900px]:py-8 min-[901px]:p-[clamp(32px,5vw,64px)]",
            visualBgClass[variant],
          ].join(" ")}
        >
          <div className="relative z-[2] max-w-[420px] motion-safe:animate-login-copy-in">
            <div className="mb-5 flex items-center gap-4">
              <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-black shadow-[0_0_0_2px_rgba(201,162,77,0.4)] max-[900px]:h-14 max-[900px]:w-14">
                {!logoFailed ? (
                  <img
                    src={brandLogo}
                    alt="Cholasys"
                    className="h-full w-full scale-[1.35] object-cover object-left"
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <span className="font-brand text-2xl text-login-gold">C</span>
                )}
              </div>
              <div>
                <p className="m-0 text-[clamp(2.2rem,5vw,3.2rem)] font-bold leading-none tracking-[-0.03em] text-white">
                  Cholasys
                </p>
                <p className="mt-2 text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-login-gold">
                  Enterprise ERP
                </p>
              </div>
            </div>
            <p className="mt-5 text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-white/70">
              {copy.label}
            </p>
            <p className="mt-3 max-w-[28ch] text-[1.05rem] font-normal leading-[1.45] text-white/80 max-[900px]:hidden">
              {copy.line}
            </p>
          </div>

          <div className="pointer-events-none absolute inset-0 z-[1]" aria-hidden="true">
            <span
              className={[
                "absolute right-[-4%] top-[18%] block h-[2px] w-[48%] origin-left opacity-[0.85] motion-safe:animate-login-slash",
                slash,
              ].join(" ")}
            />
            <span
              className={[
                "absolute right-[-10%] top-[34%] block h-[2px] w-[62%] origin-left opacity-45 [animation-delay:0.12s] motion-safe:animate-login-slash",
                slash,
              ].join(" ")}
            />
            <span
              className={[
                "absolute bottom-[22%] left-[8%] block h-[2px] w-[36%] origin-left opacity-30 [animation-delay:0.22s] motion-safe:animate-login-slash",
                slash,
              ].join(" ")}
            />
          </div>
        </aside>

        <main className="flex items-center justify-center bg-login-panel px-5 py-7 motion-safe:animate-login-side-in min-[901px]:px-12 min-[901px]:py-14">
          <div className="w-full max-w-[380px]">
            <header className="mb-7">
              <div className="mb-4 hidden items-center gap-3 max-[900px]:flex">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-login-ink">
                  {!logoFailed ? (
                    <img
                      src={brandLogo}
                      alt="Cholasys"
                      className="h-full w-full scale-[1.35] object-cover object-left"
                      onError={() => setLogoFailed(true)}
                    />
                  ) : (
                    <span className="font-brand text-lg text-login-gold">C</span>
                  )}
                </div>
                <div>
                  <p className="m-0 text-[1.35rem] font-bold leading-none text-login-ink">
                    Cholasys
                  </p>
                  <p className="mt-1 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-login-accent">
                    Enterprise ERP
                  </p>
                </div>
              </div>
              <h1 className="m-0 text-[1.6rem] font-semibold leading-[1.2] tracking-[-0.03em]">
                {title}
              </h1>
              {subtitle ? (
                <p className="mt-2.5 text-[0.92rem] leading-[1.45] text-login-muted">
                  {subtitle}
                </p>
              ) : null}
            </header>
            <div className="w-full">{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default LoginShell;
